import React, { useRef, useEffect } from 'react';
import Sketch from 'react-p5';

const P5WiseSage = () => {
  // 使用 refs 存储动画状态
  const timeRef = useRef(0);
  const particlesRef = useRef([]);
  const canvasRef = useRef(null);
  const eyeBlinkRef = useRef(0);
  const eyeStateRef = useRef(1); // 1 = 开眼, 0 = 闭眼

  // 初始化粒子系统
  const initElements = (p5, width, height) => {
    // 创建150个粒子
    particlesRef.current = Array.from({ length: 150 }, () => ({
      x: p5.random(width),
      y: p5.random(height),
      size: p5.random(1, 4),
      speed: p5.random(0.5, 2),
      angle: p5.random(p5.TWO_PI),
      radius: p5.random(50, 180),
      rotationSpeed: p5.random(0.02, 0.05) * (p5.random() > 0.5 ? 1 : -1),
      alpha: p5.random(100, 220),
      pulseSpeed: p5.random(0.03, 0.08),
      pulseOffset: p5.random(0, p5.TWO_PI),
      color: p5.color(
        120 + p5.random(60),  // R
        180 + p5.random(40),  // G 
        220 + p5.random(35),  // B
        p5.random(150, 220)   // Alpha
      ),
      attraction: p5.random(0.95, 1.05) // 粒子间吸引力
    }));
  };

  const setup = (p5, canvasParentRef) => {
    // 创建画布并设置为父元素大小
    const width = 300;
    const height = 300;
    canvasRef.current = p5.createCanvas(width, height);
    canvasRef.current.parent(canvasParentRef);
    
    // 设置基本参数
    p5.angleMode(p5.RADIANS);
    p5.colorMode(p5.RGB, 255, 255, 255, 255);
    p5.frameRate(30); // 控制帧率以优化性能
    
    // 初始化元素
    initElements(p5, width, height);
    
    // 设置画布为透明背景
    canvasRef.current.style('position', 'absolute');
    canvasRef.current.style('left', '50%');
    canvasRef.current.style('top', '50%');
    canvasRef.current.style('transform', 'translate(-50%, -50%)');
    canvasRef.current.style('z-index', '10');
  };

  const draw = (p5) => {
    // 更新时间
    timeRef.current += 0.01;
    
    // 清除画布，使用透明背景
    p5.clear();
    
    // 绘制核心球
    drawCoreBall(p5);
    
    // 绘制粒子系统
    drawParticles(p5);
  };

  // 绘制核心球
  const drawCoreBall = (p5) => {
    const centerX = p5.width / 2;
    const centerY = p5.height / 2;
    const pulse = p5.sin(timeRef.current * 2) * 0.2 + 1; // 脉动效果
    
    // 核心球基础渐变
    const coreGradient = p5.drawingContext.createRadialGradient(
      centerX, centerY, 0,
      centerX, centerY, 60 * pulse
    );
    coreGradient.addColorStop(0, 'rgba(100, 220, 240, 0.9)');
    coreGradient.addColorStop(0.5, 'rgba(80, 180, 220, 0.7)');
    coreGradient.addColorStop(0.8, 'rgba(60, 140, 200, 0.4)');
    coreGradient.addColorStop(1, 'rgba(40, 120, 180, 0)');
    
    // 高光渐变
    const highlightGradient = p5.drawingContext.createRadialGradient(
      centerX - 15, centerY - 15, 0,
      centerX - 15, centerY - 15, 30 * pulse
    );
    highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
    highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    
    // 绘制核心球主体
    p5.drawingContext.fillStyle = coreGradient;
    p5.ellipse(centerX, centerY, 120 * pulse, 120 * pulse);
    
    // 添加高光
    p5.drawingContext.fillStyle = highlightGradient;
    p5.ellipse(centerX, centerY, 120 * pulse, 120 * pulse);
    
    // 内部能量流动
    p5.push();
    p5.translate(centerX, centerY);
    p5.rotate(timeRef.current * 0.3);
    for (let i = 0; i < 8; i++) {
      p5.rotate(p5.PI / 4);
      const flowSize = 30 + p5.sin(timeRef.current * 2 + i) * 10;
      p5.fill(255, 255, 255, 50);
      p5.noStroke();
      p5.ellipse(0, 0, flowSize, flowSize * 0.6);
    }
    p5.pop();
    
    // 能量波纹
    p5.noFill();
    for (let i = 0; i < 5; i++) {
      const waveProgress = (timeRef.current * 0.5 + i * 0.3) % 1;
      const waveSize = 80 + i * 25 + p5.sin(timeRef.current * 2 + i) * 15;
      const alpha = 120 * (1 - waveProgress) - i * 15;
      const colorHue = 180 + p5.sin(timeRef.current * 0.5 + i) * 20;
      
      p5.stroke(colorHue, 200, 220, alpha);
      p5.strokeWeight(1 + waveProgress * 2);
      p5.ellipse(centerX, centerY, waveSize, waveSize);
      
      // 次级波纹
      if (i % 2 === 0) {
        const subWaveSize = waveSize * 0.9;
        p5.stroke(colorHue, 200, 220, alpha * 0.6);
        p5.strokeWeight(0.8 + waveProgress);
        p5.ellipse(centerX, centerY, subWaveSize, subWaveSize);
      }
    }
  };



  // 绘制粒子系统
  const drawParticles = (p5) => {
    const centerX = p5.width / 2;
    const centerY = p5.height / 2;
    
    // 更新并绘制所有粒子
    particlesRef.current.forEach(particle => {
      // 更新粒子位置(轨道运动+随机偏移)
      particle.angle += particle.rotationSpeed;
      const baseX = centerX + Math.cos(particle.angle) * particle.radius;
      const baseY = centerY + Math.sin(particle.angle) * particle.radius;
      
      // 添加随机偏移和物理效果
      particle.x += (baseX - particle.x) * 0.05 * particle.attraction;
      particle.y += (baseY - particle.y) * 0.05 * particle.attraction;
      
      // 粒子脉动效果
      const pulse = p5.sin(timeRef.current * particle.pulseSpeed + particle.pulseOffset);
      const size = particle.size * (1 + pulse * 0.5);
      
      // 绘制粒子(使用随机颜色)
      p5.fill(particle.color);
      p5.noStroke();
      p5.ellipse(particle.x, particle.y, size, size);
      
      // 粒子间连线(近距离粒子)
      particlesRef.current.forEach(other => {
        const dist = p5.dist(particle.x, particle.y, other.x, other.y);
        if (dist > 0 && dist < 40) {
          p5.stroke(particle.color);
          p5.strokeWeight(0.3);
          p5.line(particle.x, particle.y, other.x, other.y);
        }
      });
    });
    
    // 中心光晕效果
    const centerGlow = p5.drawingContext.createRadialGradient(
      centerX, centerY, 0,
      centerX, centerY, 120
    );
    centerGlow.addColorStop(0, 'rgba(120, 200, 220, 0.3)');
    centerGlow.addColorStop(1, 'rgba(100, 180, 210, 0)');
    p5.drawingContext.fillStyle = centerGlow;
    p5.ellipse(centerX, centerY, 240, 240);
  };


  const windowResized = (p5) => {
    // 保持画布大小不变，只调整位置
    canvasRef.current.style('left', '50%');
    canvasRef.current.style('top', '50%');
  };

  return (
    <Sketch 
      setup={setup} 
      draw={draw} 
      windowResized={windowResized}
    />
  );
};

export default P5WiseSage;
